import {
  DatasetFilters,
  Dataset,
  datasetsConstants,
  DatasetSortingOptionsConsts,
  DatasetFiltersTranslator,
} from "../../../api/Datasets";
import { DateTimeRenderer } from "../../datetime/DateTimeFormatter";
import { EntitySidebarTile, EntitySidebarTileProps } from "./EntitySidebarTile/EntitySidebarTile";
import { NotAvailable, NotSet } from "../../misc/UIconstants";
import { Dispatch, SetStateAction, useCallback, useContext, useMemo } from "react";
import { DownloadDatasetMulti } from "../../../Dataset/common/DownloadDataset/DownloadDataset";
import { SessionContext } from "../../contexts/SessionContext";
import { GetPersons } from "../../misc/EntityRenders/EntityRenderer";
import { datasetsFiltersToForm } from "../../../Dataset/Table/DatasetsFilterBar";
import { RenderTabStoreHistoryButton } from "../../tables/Tabs/history/TabStoreHistory";
import { EntityStateIndicator } from "../../badges/EntityStateIndicator/EntityStateIndicator";
import { CustomTypeLabelRenderer } from "../../../Customization/CustomTypes/generics/CustomTypeRenderer";

/**
 * Sidebar component for DetailViews
 * Author: CS
 * @param: filterquery, title
 * @returns: JSX.Element
 */

interface DatasetTileProps extends EntitySidebarTileProps<Dataset, DatasetFilters> {
  allowDownload?: boolean;
}

export const DatasetsTile = (props: DatasetTileProps) => {
  const { api } = useContext(SessionContext);
  const filters: DatasetFilters = useMemo(
    () => ({ sortBy: [{ property: "CREATION_DATE", sortDirection: "DESC" }], ...props.defaultFilters }),
    [props.defaultFilters]
  );

  const controlsUnfolded = useCallback(
    ({ filters, setFilters }: { filters: DatasetFilters; setFilters: Dispatch<SetStateAction<DatasetFilters>> }) => {
      return (
        <>
          {props.allowDownload && (
            <DownloadDatasetMulti
              btnStyle="btn btn-xs btn-primary"
              api={api}
              filters={filters}
              title="Download all datasets for this sample"
            />
          )}
          <RenderTabStoreHistoryButton
            entityConstants={datasetsConstants}
            filters={filters}
            filtersToForm={datasetsFiltersToForm}
          />
        </>
      );
    },
    [api, props.allowDownload]
  );

  const rowControls = useCallback(
    (dataset: Dataset) => {
      if (!props.allowDownload) return undefined;
      return (
        <DownloadDatasetMulti
          btnStyle="btn btn-ghost-primary btn-xs"
          api={api}
          filters={{ ids: [dataset.id] }}
          title={`Download ${dataset.name}`}
        />
      );
    },
    [api, props.allowDownload]
  );

  return (
    <EntitySidebarTile<Dataset, DatasetFilters>
      {...props}
      controlsUnfolded={props.controlsUnfolded ?? controlsUnfolded}
      entityConstants={datasetsConstants}
      orderByOptions={DatasetSortingOptionsConsts}
      translatorConsts={DatasetFiltersTranslator}
      defaultFilters={filters}
      rowLabel={(e) => (
        <div className="ellipsisContainer">
          <span>{e.name || NotAvailable}</span>
          <EntityStateIndicator entityConstants={datasetsConstants} entity={e} hideLabels />
        </div>
      )}
      rowDate={(e) => (
        <>
          {e.creationDate ? (
            <DateTimeRenderer date={e.creationDate} includeElapsed={false} includeTime={false} />
          ) : (
            NotSet
          )}
        </>
      )}
      rowInfo={(e) => <>{e.enteredBy ? <GetPersons persons={e.enteredBy} createLinks={false} /> : NotSet}</>}
      rowId={(e) => <CustomTypeLabelRenderer entity={e} />}
      rowControls={props.rowControls ?? rowControls}
    />
  );
};
